import { useState, type KeyboardEventHandler } from "react";
import { Check, Pencil, X } from "lucide-react";
import { IconButton } from "./IconButton";
import { inputClass } from "./fields/fieldStyles";
import { TagPill } from "./Pills";

interface InlineNumberEditProps {
  id: string;
  label: string;
  value: number | null;
  /** Value the override is compared against; equal values are not "overridden". */
  defaultValue?: number | null;
  onSave: (value: number | null) => Promise<boolean> | boolean;
  disabled?: boolean;
  format?: (value: number | null) => string;
  allowEmpty?: boolean;
  children?: never;
}

/** Click-to-edit numeric cell with save and cancel, keyboard reachable, marking overrides. */
export function InlineNumberEdit({ id, label, value, defaultValue, onSave, disabled, format, allowEmpty = false }: InlineNumberEditProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = draft !== null;
  const isOverridden = defaultValue !== undefined && value !== defaultValue;
  const display = format ? format(value) : value === null ? "" : String(value);

  const commit = async () => {
    if (draft === null) return;
    const trimmed = draft.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && !Number.isInteger(parsed)) return;
    if (parsed === null && !allowEmpty) return;
    setIsSaving(true);
    const saved = await onSave(parsed);
    setIsSaving(false);
    if (saved) setDraft(null);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key !== "Enter" && event.key !== "Escape") return;
    // Stop the keystroke from reaching whatever receives focus next (a confirm modal's Cancel button).
    event.preventDefault();
    if (event.key === "Enter") void commit();
    else setDraft(null);
  };

  if (!isEditing) {
    return (
      <span className="inline-flex items-center justify-end gap-1">
        <span className={`tabular-nums ${isOverridden ? "font-semibold text-brand-green-dark" : ""}`}>{display}</span>
        {isOverridden && <TagPill label="overridden" tone="green" />}
        <IconButton icon={Pencil} label={`Edit ${label}`} disabled={disabled} onClick={() => setDraft(value === null ? "" : String(value))} className="!min-h-[36px] !min-w-[36px]" />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input id={id} type="text" inputMode="numeric" autoFocus value={draft} onChange={(event) => setDraft(event.currentTarget.value)} onKeyDown={handleKeyDown} disabled={isSaving} className={`${inputClass("overridden", "sm")} w-24 text-right`} />
      <IconButton icon={Check} label={`Save ${label}`} onClick={() => void commit()} disabled={isSaving} className="!min-h-[36px] !min-w-[36px]" />
      <IconButton icon={X} label={`Cancel editing ${label}`} onClick={() => setDraft(null)} disabled={isSaving} className="!min-h-[36px] !min-w-[36px]" />
    </span>
  );
}
